import React, { memo, useEffect, useState, useRef } from "react";
import { format } from "date-fns";
import { renderToStaticMarkup } from "react-dom/server";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Printer, Languages, FileText, Loader2, Share2, ChevronDown, ChevronUp, Settings2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { adminGetClinic, getDoctorInfo, getPatient, getSettings } from "@/lib/api";
import { getBrandingImages, type BrandingImages } from "@/lib/brandingApi";
import { toast } from "@/components/ui/use-toast";
import { PrescriptionContent } from "@/components/Print/PrescriptionContent";
import { InvoiceContent } from "@/components/Print/InvoiceContent";
import type { InvoiceData } from "@/components/Print/InvoiceContent";
import { DischargeSummaryContent } from "@/components/Print/DischargeSummaryContent";
import ErrorBoundary from '@/components/ErrorBoundary';
import { useAuth } from "@/context/AuthContext.tsx";

// ── Types ────────────────────────────────────────────────────────────────────

export type PrintType     = 'prescription' | 'invoice' | 'discharge';
export type PrintLanguage = 'en' | 'hi' | 'mr';

export interface HeaderConfig {
  // image: the letterhead banner uploaded in Settings
  mode: 'auto' | 'image' | 'none' | 'custom';
  showName:    boolean;
  showAddress: boolean;
  showPhone:   boolean;
  topSpaceMm:  number;
  customText:  string;
}

const DEFAULT_HEADER_CONFIG: HeaderConfig = {
  mode: 'auto', showName: true, showAddress: true, showPhone: true,
  topSpaceMm: 20, customText: '',
};

function loadHeaderConfig(): HeaderConfig {
  try {
    const raw = localStorage.getItem('printHeaderConfig');
    if (raw) return { ...DEFAULT_HEADER_CONFIG, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_HEADER_CONFIG };
}

/** Whether this browser has header settings of its own; if not, a clinic with a letterhead banner prints with it */
function hasSavedHeaderConfig(): boolean {
  try { return !!localStorage.getItem('printHeaderConfig'); } catch { return false; }
}

function saveHeaderConfig(cfg: HeaderConfig) {
  try { localStorage.setItem('printHeaderConfig', JSON.stringify(cfg)); } catch { /* ignore */ }
}

interface PrescriptionData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  date?: string;
  [key: string]: unknown;
}

interface DischargeData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  patient_name?: string;
  admission_number?: string;
  admission_date?: string;
  discharge_date?: string;
  status?: string;
  [key: string]: unknown;
}

interface ClinicInfo  { name?: string; address?: string; phone?: string; email?: string; gstin?: string; [key: string]: unknown; }
interface DoctorInfo  { name?: string; specialization?: string; registration_no?: string; [key: string]: unknown; }
interface PatientInfo { name?: string; phone?: string; email?: string; [key: string]: unknown; }
interface Settings    { profile?: DoctorInfo; [key: string]: unknown; }

interface UnifiedPrintModalProps {
  open: boolean;
  onClose: () => void;
  type: PrintType;
  data: PrescriptionData | InvoiceData | DischargeData | null;
  title?: string;
}

// ── Translations ─────────────────────────────────────────────────────────────

const printTranslations = {
  en: {
    date: "Date", time: "Time", phone: "Phone", email: "Email",
    total: "Total", generated: "Generated on", information: "Information", details: "Details",
    morning: "Morning", afternoon: "Afternoon", evening: "Evening", night: "Night",
    beforeFood: "Before Food", afterFood: "After Food", withFood: "With Food",
    emptyStomach: "Empty Stomach", days: "days", weeks: "weeks", months: "months",
    day: "day", week: "week", month: "month",
    timesADay: "{n} times a day", everyNHours: "Every {n} hours",
    onceADay: "Once a day", twiceADay: "Twice a day", onceAWeek: "Once a week", whenNeeded: "When needed",
    asDirected: "As Directed", investigations: "Investigations:", nextVisit: "Next Visit:", advice: "Advice:",
    afterBreakfast: "After Breakfast", beforeBreakfast: "Before Breakfast", withBreakfast: "With Breakfast",
    afterLunch: "After Lunch", beforeLunch: "Before Lunch", withLunch: "With Lunch",
    afterDinner: "After Dinner", beforeDinner: "Before Dinner", withDinner: "With Dinner",
    bedtime: "At Bedtime", sos: "SOS",
    invoice: "Invoice", invoiceNumber: "Invoice Number", name: "Name", doctor: "Doctor",
    patient: "Patient", service: "Service", category: "Category", quantity: "Qty",
    unitPrice: "Unit Price", totalAmount: "Total Amount", amountPaid: "Amount Paid",
    balanceDue: "Balance Due", paymentMethod: "Payment Method", status: "Status",
    subtotal: "Subtotal", services: "Services", charges: "Charges",
  },
  hi: {
    date: "दिनांक", time: "समय", phone: "फोन", email: "ईमेल",
    total: "कुल", generated: "उत्पन्न", information: "जानकारी", details: "विवरण",
    morning: "सुबह", afternoon: "दोपहर", evening: "शाम", night: "रात",
    beforeFood: "खाना पहले", afterFood: "खाना बाद", withFood: "खाना के साथ",
    emptyStomach: "खाली पेट", days: "दिन", weeks: "सप्ताह", months: "महीने",
    day: "दिन", week: "सप्ताह", month: "महीना",
    timesADay: "दिन में {n} बार", everyNHours: "हर {n} घंटे में",
    asDirected: "निर्देशानुसार", investigations: "जांच चाहिये:", nextVisit: "अगली मुलाकात:", advice: "सलाह:",
    afterBreakfast: "नाश्ते के बाद", beforeBreakfast: "नाश्ते से पहले", withBreakfast: "नाश्ते के साथ",
    afterLunch: "दोपहर के भोजन के बाद", beforeLunch: "दोपहर के भोजन से पहले", withLunch: "दोपहर के भोजन के साथ",
    afterDinner: "रात के खाने के बाद", beforeDinner: "रात के खाने से पहले", withDinner: "रात के खाने के साथ",
    bedtime: "सोने से पहले", sos: "ज़रूरत अनुसार",
    invoice: "बिल", invoiceNumber: "बिल संख्या", name: "नाम", doctor: "डॉक्टर",
    patient: "मरीज़", service: "सेवा", category: "श्रेणी", quantity: "मात्रा",
    unitPrice: "दर", totalAmount: "कुल राशि", amountPaid: "भुगतान", balanceDue: "बकाया",
    paymentMethod: "भुगतान विधि", status: "स्थिति", subtotal: "उप-योग",
    services: "सेवाएं", charges: "शुल्क",
  },
  mr: {
    date: "दिनांक", time: "वेळ", phone: "फोन", email: "ईमेल",
    total: "एकूण", generated: "तयार केले", information: "माहिती", details: "तपशील",
    morning: "सकाळी", afternoon: "दुपारी", evening: "संध्याकाळी", night: "रात्री",
    beforeFood: "जेवणापूर्वी", afterFood: "जेवणानंतर", withFood: "जेवणासह",
    emptyStomach: "रिकाम्या पोटी", days: "दिवस", weeks: "आठवडे", months: "महिने",
    day: "दिवस", week: "आठवडा", month: "महिना",
    timesADay: "दिवसातून {n} वेळा", everyNHours: "दर {n} तासांनी",
    asDirected: "निर्देशानुसार", investigations: "तपासणी आवश्यक:", nextVisit: "पुढची भेट:", advice: "सल्ला:",
    afterBreakfast: "नाश्त्यानंतर", beforeBreakfast: "नाश्त्यापूर्वी", withBreakfast: "नाश्त्यासह",
    afterLunch: "दुपारच्या जेवणानंतर", beforeLunch: "दुपारच्या जेवणापूर्वी", withLunch: "दुपारच्या जेवणासह",
    afterDinner: "रात्रीच्या जेवणानंतर", beforeDinner: "रात्रीच्या जेवणापूर्वी", withDinner: "रात्रीच्या जेवणासह",
    bedtime: "झोपण्यापूर्वी", sos: "गरजेनुसार",
    invoice: "बिल", invoiceNumber: "बिल क्रमांक", name: "नाव", doctor: "डॉक्टर",
    patient: "रुग्ण", service: "सेवा", category: "प्रकार", quantity: "संख्या",
    unitPrice: "दर", totalAmount: "एकूण रक्कम", amountPaid: "भरलेली रक्कम",
    balanceDue: "उरलेली रक्कम", paymentMethod: "पेमेंट पद्धत", status: "स्थिती",
    subtotal: "उप-एकूण", services: "सेवा", charges: "शुल्क",
  },
};

// ── Iframe HTML builder ───────────────────────────────────────────────────────

function buildIframeHtml(
  type: PrintType,
  data: PrescriptionData | InvoiceData | DischargeData,
  t: Record<string, string>,
  language: PrintLanguage,
  clinicInfo: ClinicInfo | null,
  doctorInfo: DoctorInfo | null,
  patientInfo: PatientInfo | null,
  settings: Settings | null,
  currentUser: { name?: string } | null,
  headerConfig: HeaderConfig,
  branding: BrandingImages | null,
  docTitle: string,
): string {
  const resolvedDoctor = settings?.profile ?? doctorInfo;
  // The clinic's logo and letterhead travel with its details; the doctor's signature goes to the signature line
  const clinic = clinicInfo && { ...clinicInfo, logo: branding?.logo ?? null, letterhead: branding?.letterhead ?? null };
  const signatureImage = branding?.signature ?? null;

  let body: string;
  if (type === 'prescription') {
    body = renderToStaticMarkup(
      React.createElement(PrescriptionContent, {
        data, translations: t, language,
        clinicInfo: clinic, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig, signatureImage,
      })
    );
  } else if (type === 'discharge') {
    body = renderToStaticMarkup(
      React.createElement(DischargeSummaryContent, {
        data, translations: t, language,
        clinicInfo: clinic, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig, signatureImage,
      })
    );
  } else {
    body = renderToStaticMarkup(
      React.createElement(InvoiceContent, {
        data: data as InvoiceData, translations: t, language,
        clinicInfo: clinic, doctorInfo: resolvedDoctor, patientInfo, settings, currentUser, headerConfig,
      })
    );
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${docTitle.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; }
  html, body {
    font-family: Arial, 'Helvetica Neue', sans-serif;
    font-size: 11px;
    line-height: 1.4;
    color: #000;
    background: #fff;
    margin: 0;
    padding: 12mm 15mm;
    /* the padding repeats on every printed page, so page 2 doesn't start at the paper's edge */
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { padding: 10mm; }
  }
</style>
</head>
<body>${body}</body>
</html>`;
}

/** The page as an A4 PDF, drawn from the preview, so it can be sent as a file (e.g. on WhatsApp) */
async function pageToPdf(doc: Document, filename: string): Promise<File> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas-pro'), import('jspdf')]);
  const canvas = await html2canvas(doc.body, { scale: 2, backgroundColor: '#ffffff' });
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pagePx = canvas.width * 297 / 210;
  // Longer documents: one A4 slice of the page per PDF page
  for (let y = 0; y < canvas.height - 8; y += pagePx) {
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = Math.min(pagePx, canvas.height - y);
    slice.getContext('2d')!.drawImage(canvas, 0, -y);
    if (y > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, slice.height * 210 / canvas.width);
  }
  return new File([pdf.output('blob')], filename, { type: 'application/pdf' });
}

function downloadFile(file: File) {
  const url = URL.createObjectURL(file);
  Object.assign(document.createElement('a'), { href: url, download: file.name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Prints the document from a tab of its own. Tablets (iPad Safari, Android Chrome) print the whole app screen
 * when asked to print a frame inside it, and browsers name a saved PDF after the page's own title.
 * Returns false when the browser blocked the new tab.
 */
function printInNewTab(html: string): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;
  const doc = win.document;
  doc.open();
  doc.write(html);
  doc.close();

  // On screen only: browsers that don't wait at print() (tablets, Firefox) leave this tab open
  const bar = doc.createElement('div');
  bar.className = 'print-tab-bar';
  bar.innerHTML = '<style>.print-tab-bar{position:fixed;top:0;left:0;right:0;display:flex;gap:8px;justify-content:flex-end;'
    + 'padding:8px 12px;background:#f1f5f9;border-bottom:1px solid #cbd5e1;font:14px Arial,sans-serif}'
    + '.print-tab-bar button{padding:8px 16px;border-radius:6px;border:1px solid #94a3b8;background:#fff;font:inherit}'
    + '.print-tab-bar button.primary{background:#0f766e;border-color:#0f766e;color:#fff}'
    + 'body{margin-top:52px!important}@media print{.print-tab-bar{display:none}body{margin-top:0!important}}</style>'
    + '<button type="button">Close</button><button type="button" class="primary">Print again</button>';
  const [closeBtn, againBtn] = Array.from(bar.querySelectorAll('button'));
  closeBtn.onclick = () => win.close();
  againBtn.onclick = () => win.print();
  doc.body.prepend(bar);

  // Logo, letterhead and signature images must be decoded before the page is printed
  Promise.all(Array.from(doc.images).map(img => img.decode().catch(() => undefined))).then(() => {
    let printed = false;
    win.addEventListener('afterprint', () => { printed = true; }, { once: true });
    win.focus();
    win.print();
    // Desktop browsers wait at print() until the dialog closes: then the tab's job is done
    if (printed) win.close();
  });
  return true;
}

// ── Component ────────────────────────────────────────────────────────────────

function UnifiedPrintModal({ open, onClose, type, data, title }: Readonly<UnifiedPrintModalProps>) {

  const { currentUser } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const htmlRef   = useRef('');

  const [printLanguage, setPrintLanguage]   = useState<PrintLanguage>('en');
  const [isPrinting, setIsPrinting]         = useState(false);
  const [isSavingPDF, setIsSavingPDF]       = useState(false);
  const [isSharing, setIsSharing]           = useState(false);
  const [isLoading, setIsLoading]           = useState(false);
  const [headerConfig, setHeaderConfig]     = useState<HeaderConfig>(loadHeaderConfig);
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [showSettings, setShowSettings]     = useState(false);

  // The preview shows the A4 page (794px wide) scaled to fit, so phones and tablets see the whole page
  const previewRef = useRef<HTMLDivElement>(null);
  const [pageScale, setPageScale]   = useState(1);
  const [pageHeight, setPageHeight] = useState(1123);
  // Tap the page to read it at full size (scrolls sideways), tap again to fit
  const [zoomed, setZoomed]         = useState(false);
  const scale = zoomed ? 1 : pageScale;
  useEffect(() => {
    const el = previewRef.current;
    if (!open || !el) return;
    const ro = new ResizeObserver(([entry]) => setPageScale(Math.min(1, entry.contentRect.width / 794)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, isLoading]);

  const updateHeaderConfig = (patch: Partial<HeaderConfig>) => {
    setHeaderConfig(prev => {
      const next = { ...prev, ...patch };
      saveHeaderConfig(next);
      return next;
    });
  };

  const [clinicInfo,  setClinicInfo]  = useState<ClinicInfo  | null>(null);
  const [doctorInfo,  setDoctorInfo]  = useState<DoctorInfo  | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [settings,    setSettings]    = useState<Settings    | null>(null);
  const [branding,    setBranding]    = useState<BrandingImages | null>(null);

  // Load clinic / doctor / patient data when modal opens
  useEffect(() => {
    if (!open) return;

    setClinicInfo(null);
    setDoctorInfo(null);
    setPatientInfo(null);
    setSettings(null);
    setBranding(null);
    setIsLoading(true);

    if (!data) { setIsLoading(false); return; }

    const d = data as PrescriptionData;
    if (!d.doctor_id || !d.clinic_id) { setIsLoading(false); return; }

    (async () => {
      try {
        const settingsData = await getSettings(d.doctor_id, d.clinic_id);
        setSettings(settingsData);
        if (!settingsData?.profile?.name) {
          const doctor = await getDoctorInfo(d.doctor_id);
          setDoctorInfo(doctor);
        }
        const clinic = await adminGetClinic();
        setClinicInfo(clinic);
        // Logo, letterhead and the doctor's signature; the print still works without them
        const images = await getBrandingImages(d.doctor_id).catch(() => null);
        setBranding(images);
        if (images?.letterhead && !hasSavedHeaderConfig()) setHeaderConfig(c => ({ ...c, mode: 'image' }));
        if (d.patient_id) {
          const patient = await getPatient(d.patient_id);
          setPatientInfo(patient);
        }
      } catch {
        toast({ title: "Data Loading Error", description: "Some information may not display correctly", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open, data]);

  // Write HTML into the iframe whenever content or language changes
  useEffect(() => {
    if (isLoading || !data || !iframeRef.current) return;
    const t = printTranslations[printLanguage];
    const html = buildIframeHtml(
      type, data, t, printLanguage,
      clinicInfo, doctorInfo, patientInfo, settings, currentUser ?? null,
      headerConfig, branding, generatePrintTitle(),
    );
    htmlRef.current = html;
    const doc = iframeRef.current.contentDocument ?? iframeRef.current.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    // Longer documents run past one page: show all of it
    setPageHeight(Math.max(1123, doc.documentElement.scrollHeight));
  }, [isLoading, data, type, printLanguage, clinicInfo, doctorInfo, patientInfo, settings, currentUser, headerConfig, branding]);

  const getDocDate = () => {
    if (!data) return new Date();
    const inv = data as InvoiceData;
    const rx  = data as PrescriptionData;
    const dis = data as DischargeData;
    if (type === 'discharge') return new Date(dis.discharge_date ?? dis.admission_date ?? new Date());
    return new Date(inv.invoice_date ?? rx.date ?? new Date());
  };

  // One name for the printed page, a saved or shared PDF: "Prescription - Meera Kulkarni - 02 Oct 2026"
  const generatePrintTitle = () => {
    const date        = format(getDocDate(), 'dd MMM yyyy');
    const patientName = patientInfo?.name ?? (data as PrescriptionData)?.patient_name ?? 'Patient';
    if (type === 'prescription') return `Prescription - ${patientName} - ${date}`;
    if (type === 'discharge')    return `Discharge Summary - ${patientName} - ${date}`;
    const invoiceRef = (data as InvoiceData)?.invoice_number;
    if ((data as InvoiceData)?.doc_title) return `${(data as InvoiceData).doc_title} - ${patientName} - ${date}`;
    return `${invoiceRef ? `Invoice ${invoiceRef}` : 'Invoice'} - ${patientName} - ${date}`;
  };

  // Characters Windows doesn't allow in a file name
  const generateFilename = () => `${generatePrintTitle().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim()}.pdf`;

  const handlePrint = async () => {
    if (isPrinting || isLoading) return;
    // The new tab must open straight from the tap, or the browser blocks it
    if (htmlRef.current && printInNewTab(htmlRef.current)) return;
    setIsPrinting(true);
    // Logo, letterhead and signature images must be decoded before the page is printed
    const images = Array.from(iframeRef.current?.contentDocument?.images ?? []);
    await Promise.all(images.map(img => img.decode().catch(() => undefined)));
    try {
      iframeRef.current?.contentWindow?.print();
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSavePDF = async () => {
    if (isSavingPDF || isLoading) return;
    return savePdfInBrowser();
  };

  const savePdfInBrowser = async () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const filename = generateFilename();
    const picker = (globalThis as any).showSaveFilePicker;
    try {
      // The dialog must open straight from the tap, before the PDF is made
      const handle = picker && await picker({ suggestedName: filename, types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }] });
      setIsSavingPDF(true);
      await Promise.all(Array.from(doc.images).map(img => img.decode().catch(() => undefined)));
      const file = await pageToPdf(doc, filename);
      if (handle) {
        const out = await handle.createWritable();
        await out.write(file);
        await out.close();
      } else {
        downloadFile(file);
      }
      toast({ title: "PDF saved", description: handle?.name ?? file.name });
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        toast({ title: "PDF Export Error", description: "Failed to save PDF. Please try again.", variant: "destructive" });
      }
    } finally {
      setIsSavingPDF(false);
    }
  };

  const handleShare = async () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || isSharing || isLoading) return;
    setIsSharing(true);
    try {
      await Promise.all(Array.from(doc.images).map(img => img.decode().catch(() => undefined)));
      const file = await pageToPdf(doc, generateFilename());
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: generatePrintTitle() });
      } else {
        // No share sheet for files here (most desktop browsers): save the PDF to attach in WhatsApp or email
        downloadFile(file);
        toast({ title: "PDF downloaded", description: "Attach it in WhatsApp or email to send it to the patient" });
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        toast({ title: "Share failed", description: "Could not make the PDF. Try Print instead.", variant: "destructive" });
      }
    } finally {
      setIsSharing(false);
    }
  };

  const typeLabel = type === 'prescription' ? 'Prescription' : type === 'discharge' ? 'Discharge Summary' : 'Invoice';

  // Header mode, language, PDF options: the side panel on desktop, a sheet over the bottom bar on phones and tablets
  const settingsBody = (
    <>
      {/* Header Settings */}
      <div className="space-y-2 border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50 transition-colors"
          onClick={() => setHeaderExpanded(e => !e)}
        >
          <span>Header Settings</span>
          {headerExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {headerExpanded && (
          <div className="px-3 pb-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Header mode</Label>
              <Select value={headerConfig.mode === 'image' && !branding?.letterhead ? 'auto' : headerConfig.mode} onValueChange={(v) => updateHeaderConfig({ mode: v as HeaderConfig['mode'] })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (from database)</SelectItem>
                  {branding?.letterhead && <SelectItem value="image">Letterhead image</SelectItem>}
                  <SelectItem value="none">None (pre-printed letterhead)</SelectItem>
                  <SelectItem value="custom">Custom text</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {headerConfig.mode === 'auto' && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Show fields</Label>
                {[
                  { id: 'showName',    label: 'Clinic name' },
                  { id: 'showAddress', label: 'Address' },
                  { id: 'showPhone',   label: 'Phone' },
                ].map(({ id, label }) => (
                  <div key={id} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={headerConfig[id as keyof HeaderConfig] as boolean}
                      onCheckedChange={(c) => updateHeaderConfig({ [id]: c === true })}
                    />
                    <Label htmlFor={id} className="text-xs font-normal cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
            )}

            {headerConfig.mode === 'none' && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Space reserved for letterhead (mm)</Label>
                <input
                  type="number"
                  min={0}
                  max={80}
                  value={headerConfig.topSpaceMm}
                  onChange={(e) => updateHeaderConfig({ topSpaceMm: Number(e.target.value) })}
                  className="w-full h-8 rounded-md border border-field bg-background px-3 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            )}

            {headerConfig.mode === 'custom' && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Header text</Label>
                <Textarea
                  rows={4}
                  placeholder={"Clinic name\nAddress line 1\nPhone: 9999999999"}
                  value={headerConfig.customText}
                  onChange={(e) => updateHeaderConfig({ customText: e.target.value })}
                  className="text-xs resize-none"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Language */}
      {type !== 'discharge' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Languages className="w-4 h-4" />
            Print Language
          </Label>
          <Select value={printLanguage} onValueChange={(v) => setPrintLanguage(v as PrintLanguage)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="hi">हिंदी (Hindi)</SelectItem>
              <SelectItem value="mr">मराठी (Marathi)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

    </>
  );

  return (
    <ErrorBoundary>
      <Dialog open={open} onOpenChange={() => {}}>
        {/* Phones / tablets: full screen, the page first; desktop: page left, settings right */}
        <DialogContent
          className="max-w-[96vw] max-h-[96vh] w-full h-full flex flex-col lg:flex-row p-0 gap-0 max-lg:max-w-none max-lg:max-h-none max-lg:h-[100dvh] max-lg:rounded-none max-lg:border-0"
          showClose={false}
        >

          {/* ── Preview iframe ──────────────────────────────────────────── */}
          <div className="flex-1 min-h-0 flex flex-col lg:border-r border-border overflow-hidden bg-muted/10">
            <div className="bg-muted/40 px-4 py-2 border-b flex-shrink-0 flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-muted-foreground truncate">
                {title || `${typeLabel} Preview`}
              </p>
              <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden h-9 w-9 -mr-2 flex-shrink-0" aria-label="Close">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-auto p-3 sm:p-6 bg-muted/20">
              <div ref={previewRef} className="w-full">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <Loader2 className="w-7 h-7 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading preview…</p>
                  </div>
                ) : (
                  // The A4 page at its real width (794px), scaled down to fit — the whole page, as it will print
                  <div
                    style={{ width: 794 * scale, height: pageHeight * scale, margin: '0 auto', position: 'relative' }}
                    className={pageScale < 1 ? (zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in') : undefined}
                  >
                    <iframe
                      ref={iframeRef}
                      title="print-preview"
                      style={{
                        width: '794px',
                        height: `${pageHeight}px`,
                        border: 'none',
                        boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
                        backgroundColor: '#fff',
                        display: 'block',
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                      }}
                    />
                    {/* Taps land on the iframe's own document, so a transparent layer catches them */}
                    {pageScale < 1 && (
                      <button
                        type="button"
                        onClick={() => setZoomed(z => !z)}
                        aria-label={zoomed ? 'Fit page to screen' : 'Show page at full size'}
                        className="absolute inset-0 w-full h-full bg-transparent"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Controls — desktop side panel ───────────────────────────── */}
          <div className="hidden lg:block w-72 bg-background p-4 space-y-4 overflow-y-auto flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-foreground text-sm">Print Settings</h2>
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <X className="w-4 h-4" />
              </Button>
            </div>

            {settingsBody}

            {/* Actions */}
            <div className="space-y-2 pt-2">
              <Button onClick={handlePrint} disabled={isPrinting || isLoading} className="w-full">
                {isPrinting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Printing…</>
                  : <><Printer className="w-4 h-4 mr-2" />Print {typeLabel}</>}
              </Button>

              <Button onClick={handleShare} disabled={isSharing || isLoading} variant="outline" className="w-full">
                {isSharing
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Making PDF…</>
                  : <><Share2 className="w-4 h-4 mr-2" />Share PDF</>}
              </Button>

              <Button onClick={handleSavePDF} disabled={isSavingPDF || isLoading} variant="outline" className="w-full text-primary border-primary/30 hover:bg-primary/5">
                {isSavingPDF
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  : <><FileText className="w-4 h-4 mr-2" />Save as PDF</>}
              </Button>

              <Button variant="outline" onClick={onClose} className="w-full">
                <X className="w-4 h-4 mr-2" />Cancel
              </Button>
            </div>
          </div>

          {/* ── Controls — phones / tablets: settings sheet + bottom bar ─ */}
          <div className="lg:hidden flex-shrink-0 border-t border-border bg-background">
            {showSettings && (
              <div className="max-h-[50dvh] overflow-y-auto p-4 space-y-4 border-b border-border">
                {settingsBody}
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <Button variant="outline" onClick={() => setShowSettings(s => !s)} aria-expanded={showSettings} className="gap-1.5">
                <Settings2 className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
                {showSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
              <Button variant="outline" onClick={handleShare} disabled={isSharing || isLoading} aria-label="Share PDF" className="gap-1.5">
                {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Button variant="outline" onClick={handleSavePDF} disabled={isSavingPDF || isLoading} aria-label="Save as PDF" className="gap-1.5">
                {isSavingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                <span className="hidden sm:inline">PDF</span>
              </Button>
              <Button onClick={handlePrint} disabled={isPrinting || isLoading} className="flex-1 gap-1.5">
                {isPrinting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                Print
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
}

export default memo(UnifiedPrintModal);
